import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, NavLink, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import ArtisanOnboarding from './ArtisanOnboarding';
import MobileBottomNav from '../common/MobileBottomNav';
import './ArtisanLayout.css';

function ArtisanLayout() {
    const navigate = useNavigate();
    const [user] = useState(() => {
        const saved = localStorage.getItem('user');
        return saved ? JSON.parse(saved) : null;
    });
    const [checkingProfile, setCheckingProfile] = useState(true);
    const [profileComplete, setProfileComplete] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState('ACTIVE'); // Default to active until checked

    useEffect(() => {
        checkProfileCompleteness();
    }, []);

    const checkProfileCompleteness = async () => {
        try {
            const res = await api.get('/api/artisan/profile/completeness');
            if (res.ok) {
                const data = await res.json();
                setProfileComplete(data.complete === true);
                if (data.status) setVerificationStatus(data.status);
            } else {
                setProfileComplete(false);
            }
        } catch (e) {
            setProfileComplete(false);
        } finally {
            setCheckingProfile(false);
        }
    };

    if (!user || user.role !== 'ARTISAN') {
        return <Navigate to="/" replace />;
    }

    if (checkingProfile) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#faf7f2' }}>
                <p style={{ color: '#6b7c74', fontSize: '1.1rem' }}>Loading...</p>
            </div>
        );
    }

    // Gate: force onboarding wizard if profile is incomplete
    if (!profileComplete) {
        return <ArtisanOnboarding user={user} onComplete={() => setProfileComplete(true)} />;
    }

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    return (
        <div className="artisan-layout">
            <aside className="artisan-sidebar">
                <div className="sidebar-header">
                    <h2>Glaminant</h2>
                    <span className="badge">Artisan</span>
                </div>

                <nav className="sidebar-nav">
                    <NavLink to="/artisan/dashboard" className={({ isActive }) => isActive ? 'active' : ''}>
                        📅 Dashboard
                    </NavLink>
                    <NavLink
                        to="/artisan/profile"
                        end
                        className={({ isActive }) => {
                            // Profile is active only if base path matches AND tab is NOT schedule
                            if (isActive && !window.location.search.includes('tab=schedule')) return 'active';
                            return '';
                        }}
                    >
                        👤 Profile
                    </NavLink>
                    <NavLink to="/artisan/requests" className={({ isActive }) => isActive ? 'active' : ''}>
                        🔔 Requests
                    </NavLink>
                    {/* Placeholder for Availability */}
                    <NavLink
                        to="/artisan/profile?tab=schedule"
                        className={({ isActive }) => {
                            // Check if current location matches AND search params match (simple check for now)
                            if (isActive && window.location.search.includes('tab=schedule')) return 'active';
                            return '';
                        }}
                    >
                        ⚙️ Availability
                    </NavLink>
                </nav>

                <div className="sidebar-footer">
                    <div className="user-info">
                        <div className="avatar">{user.name.charAt(0)}</div>
                        <span>{user.name}</span>
                    </div>
                    <button onClick={handleLogout} className="btn-logout">Sign Out</button>
                </div>
            </aside>

            <main className="artisan-content">
                {verificationStatus === 'PENDING_VERIFICATION' && (
                    <div className="verification-banner">
                        <span role="img" aria-label="info">ℹ️</span>
                        <span>
                            <strong>Profile Under Review:</strong> Your profile is currently being reviewed by our team.
                            You will be notified once you are approved to start receiving bookings.
                        </span>
                    </div>
                )}
                <Outlet context={{ user, verificationStatus }} />
            </main>
            <MobileBottomNav />
        </div>
    );
}

export default ArtisanLayout;

